/**
 * Kleiner Cache für die klingenden Töne der Griffschrift-**Ausgabe**, damit der Hover-Tooltip
 * dieselbe Note nicht wiederholt über `POST /api/note-grips` erfragt.
 *
 * Der Aufruf ist teuer genug, dass sich das lohnt: serverseitig ein vollständiger
 * music21-Import je Request (`parse_musicxml` schreibt dafür eine Temp-Datei), clientseitig ein
 * komplettes `DOMParser`-Reparse in `buildNoteGripsRequest` (`partIndexForNote` liest das ganze
 * MusicXML, nur um `part_index` aufzulösen) – und das auf dem Main-Thread, getrieben von
 * `mousemove`.
 *
 * **Die Korrektheit hängt an der Generation (kritisch – nicht auf einen reinen `noteKey`-Cache
 * rückbauen):** `noteKey` (`partId|measure|voice|voiceNoteIndex|staff`) adressiert eine Note nur
 * innerhalb *einer* Fassung des Dokuments. Griff-Tausch, Bass-/Gesangstext-Edit, Quelltext-Editor
 * und Undo schreiben jeweils einen neuen `musicXml`-String; ein Struktur-Edit (Teilen/Löschen)
 * verschiebt zusätzlich die Bedeutung des Schlüssels. Der String selbst ist deshalb die
 * Generation – ein Eintrag kann eine Änderung nicht überleben. Das ist zugleich der Grund, warum
 * **nicht** gehasht wird: der Vergleich ist ein Referenzvergleich auf denselben durchgereichten
 * String, kein Scan über 100–500 KB.
 */
import {noteKey} from '@/composables/musicXmlDom';
import type {NoteInfo} from '@/composables/musicXmlDom';

/** Obergrenze, ab der die Generation komplett geleert wird. */
export const SOUNDING_NOTE_CACHE_MAX_ENTRIES = 200;

export interface SoundingNoteCache {
    /** Klingende Töne der Note in **dieser** Dokument-Fassung, sonst `undefined`. Reiner Lesezugriff
     *  (darf aus einem `computed` gerufen werden). */
    get(musicXml: string, note: NoteInfo): string[] | undefined;
    /** Legt den Eintrag an; eine neue Fassung verwirft dabei die vorherige vollständig. */
    set(musicXml: string, note: NoteInfo, soundingNotes: string[]): void;
    readonly size: number;
}

export function createSoundingNoteCache(
    maxEntries: number = SOUNDING_NOTE_CACHE_MAX_ENTRIES,
): SoundingNoteCache {
    let generation: string | null = null;
    const entries = new Map<string, string[]>();

    return {
        get(musicXml, note) {
            if (musicXml !== generation) return undefined;
            return entries.get(noteKey(note));
        },
        set(musicXml, note, soundingNotes) {
            if (musicXml !== generation) {
                generation = musicXml;
                entries.clear();
            }
            // Überlauf: ganz leeren statt LRU-Buchhaltung – vorhersagbar, und der Tooltip baut den
            // Arbeitssatz beim Weiterhovern ohnehin in wenigen Requests wieder auf.
            if (entries.size >= maxEntries) entries.clear();
            entries.set(noteKey(note), soundingNotes);
        },
        get size() {
            return entries.size;
        },
    };
}
